import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import DatePicker from '../../components/DatePicker/DatePicker';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

const PAYMENT_TYPES = ['Cash', 'Cheque', 'Bank Transfer', 'UPI'];

function CreateExpense() {
    const { expenseId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(expenseId));
    const [vendors, setVendors] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        description: '',
        paymentType: 'Cash',
        vendorId: '',
        clientId: '',
        projectId: ''
    });

    useEffect(() => {
        withLoading(async () => {
            const vendorsRes = await api.get('/vendors');
            setVendors(vendorsRes.data);

            const clientsRes = await api.get('/clients');
            setClients(clientsRes.data);

            const projectsRes = await api.get('/projects');
            setProjects(projectsRes.data);

            if (expenseId) {
                const expenseRes = await api.get(`/expenses/${expenseId}`);
                setFormData({
                    date: expenseRes.data.date ? expenseRes.data.date.split('T')[0] : '',
                    amount: String(expenseRes.data.amount ?? ''),
                    description: expenseRes.data.description || '',
                    paymentType: expenseRes.data.paymentType || 'Cash',
                    vendorId: expenseRes.data.vendorId ? String(expenseRes.data.vendorId) : '',
                    clientId: expenseRes.data.clientId ? String(expenseRes.data.clientId) : '',
                    projectId: expenseRes.data.projectId ? String(expenseRes.data.projectId) : ''
                });
            }
        }).catch((error) => {
            const message = error.response?.data?.Error || 'Error loading expense data';
            alert(message);
        });
    }, [expenseId, withLoading]);

    const filteredProjects = useMemo(() => {
        if (!formData.clientId) {
            return [];
        }
        return projects.filter(
            (project) => String(project.clientId) === String(formData.clientId)
        );
    }, [projects, formData.clientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const next = { ...prev, [name]: value };

            if (name === 'clientId') {
                const projectStillValid = prev.projectId && projects.some(
                    (project) =>
                        String(project.id) === String(prev.projectId) &&
                        String(project.clientId) === String(value)
                );
                if (!projectStillValid) {
                    next.projectId = '';
                }
            }

            return next;
        });
    };

    const buildPayload = () => ({
        date: formData.date,
        amount: parseFloat(formData.amount),
        description: formData.description.trim(),
        paymentType: formData.paymentType,
        vendorId: formData.vendorId ? Number(formData.vendorId) : null,
        clientId: formData.clientId ? Number(formData.clientId) : null,
        projectId: formData.projectId ? Number(formData.projectId) : null
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = buildPayload();
        const apiCall = expenseId
            ? api.put(`/expenses/${expenseId}`, payload)
            : api.post('/expenses', payload);

        apiCall
            .then(() => {
                navigate('/financials/expense');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error saving expense';
                alert(message);
            });
    };

    const handleDelete = () => {
        if (!window.confirm('Are you sure you want to delete this expense entry?')) return;

        api.delete(`/expenses/${expenseId}`)
            .then(() => {
                navigate('/financials/expense');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error deleting expense';
                alert(message);
            });
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{expenseId ? 'Modify expense' : 'Add expense'}</h1>
                        <p className="form-page-subtitle">
                            {expenseId
                                ? 'Update expense details'
                                : 'Record a new expense entry'}
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/financials/expense')}
                        >
                            Back to list
                        </button>
                        {expenseId && (
                            <button
                                type="button"
                                className="form-page-btn-danger"
                                onClick={handleDelete}
                            >
                                Delete expense
                            </button>
                        )}
                        <button
                            type="submit"
                            form="create-expense-form"
                            className="form-page-btn"
                        >
                            {expenseId ? 'Update expense' : 'Save expense'}
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Expense details</h2>
                            <p className="form-page-section-hint">Date, amount, payment type, and description</p>
                        </div>
                    </div>

                    <form id="create-expense-form" onSubmit={handleSubmit}>
                        <div className="form-page-grid cols-3">
                            <div className="form-page-field">
                                <label htmlFor="date">Date</label>
                                <DatePicker
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="amount">Amount</label>
                                <input
                                    type="number"
                                    id="amount"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    required
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="paymentType">Payment type</label>
                                <select
                                    id="paymentType"
                                    name="paymentType"
                                    value={formData.paymentType}
                                    onChange={handleChange}
                                    required
                                >
                                    {PAYMENT_TYPES.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    required
                                    placeholder="Enter expense description"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="vendorId">Vendor (optional)</label>
                                <select
                                    id="vendorId"
                                    name="vendorId"
                                    value={formData.vendorId}
                                    onChange={handleChange}
                                >
                                    <option value="">No vendor</option>
                                    {vendors.map((vendor) => (
                                        <option key={vendor.id} value={vendor.id}>
                                            {vendor.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="clientId">Client (optional)</label>
                                <select
                                    id="clientId"
                                    name="clientId"
                                    value={formData.clientId}
                                    onChange={handleChange}
                                >
                                    <option value="">No client</option>
                                    {clients.map((client) => (
                                        <option key={client.id} value={client.id}>
                                            {client.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="projectId">Project (optional)</label>
                                <select
                                    id="projectId"
                                    name="projectId"
                                    value={formData.projectId}
                                    onChange={handleChange}
                                    disabled={!formData.clientId}
                                >
                                    <option value="">
                                        {formData.clientId ? 'No project' : 'Select a client first'}
                                    </option>
                                    {filteredProjects.map((project) => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </PageLoadingArea>
    );
}

export default CreateExpense;
